import { ActionButton, isActionButton } from "components/actionButton/actionButton";
import {
    Component,
    DataHolderComponent,
    InputComponent,
    isComponentThatCanDisableOptions,
    isComponentWithOptions,
    isDisableableComponent,
    isHideableComponent,
    isInputComponent,
    isValueComponent,
    ValidatorComponent,
} from "components/components/Component";
import {
    Dependency,
    DependencyType,
    isExpressionDependency,
    isTableColumnDepedency,
} from "components/restrictions/dependencies/Dependency";
import { isTable } from "components/table/table";
import { FormRestrictions } from "../Restrictions";
import { DependencyEvaluator } from "./DependencyEvaluator";
import { ExpressionDependencyRunner } from "./ExpressionDependency";

export class DependencyRunner {
    private readonly restrictions: FormRestrictions;
    private readonly parentModel: any;
    constructor(parentModel: any, restrictions: FormRestrictions) {
        this.parentModel = parentModel;
        this.restrictions = restrictions;
    }

    private findDependenciesForComponent(component: Component): Dependency[] {
        if (isValueComponent(component)) {
            if (this.restrictions[component.definition.property] != null
                && this.restrictions[component.definition.property].dependencies != null) {
                return this.restrictions[component.definition.property].dependencies;
            }
        } else if (this.restrictions[component.definition.id] != null
                && this.restrictions[component.definition.id].dependencies != null) {
                return this.restrictions[component.definition.id].dependencies;
            }
        return [];
    }

    public applyDependencies(component: Component, holder: ValidatorComponent & DataHolderComponent): void {
        if (this.restrictions == null) {
            return;
        }
        for (let dependency of this.findDependenciesForComponent(component)) {
            const matchResult = DependencyEvaluator.or(dependency.conditions, this.parentModel);

            this.applyDependency(
                component,
                dependency,
                matchResult.matches,
                holder,
                matchResult.component,
            );
        }
    }

    private applyDependency(
        component: Component,
        dependency: Dependency,
        matched: (boolean | string | number),
        holder: (ValidatorComponent & DataHolderComponent),
        remoteValue: any,
    ): void {
        let type = dependency.type;
        if (isDisableableComponent(component)) {
            if (type === DependencyType.enabled) {
                component.setReadOnly(!matched as boolean);
            } else if (type === DependencyType.disabled) {
                component.setReadOnly(matched as boolean);
            }
        } else if (isActionButton(component)) {
            if (type === DependencyType.enabled) {
                if (matched) {
                    component.enable();
                } else {
                    component.disable();
                }
            } else if (type === DependencyType.disabled) {
                if (matched) {
                    component.disable();
                } else {
                    component.enable();
                }
            }
        }
        if (isInputComponent(component)) {
            if (type === DependencyType.required && holder != null) {
                DependencyRunner.setRequired(component, holder, matched as boolean);
            } else if (type === DependencyType.optional && holder != null) {
                DependencyRunner.setRequired(component, holder, !matched as boolean);
            }
        }
        if (isTable(component) && isTableColumnDepedency(dependency)) {
            if (type === DependencyType.enabled) {
                component.setReadOnly(!matched as boolean, dependency.targetColumn);
            } else if (type === DependencyType.disabled) {
                component.setReadOnly(matched as boolean, dependency.targetColumn);
            }
        }
        if (isHideableComponent(component)) {
            if (type === DependencyType.hidden) {
                if (matched) {
                    component.hide();
                } else {
                    component.show();
                }
            }

            if (type === DependencyType.visible) {
                if (matched) {
                    component.show();
                } else {
                    component.hide();
                }
            }
        }

        if (isValueComponent(component)) {
            if (type === DependencyType.value && matched) {
                let newValue;
                if (isExpressionDependency(dependency)) {
                    newValue = ExpressionDependencyRunner.run(dependency.expression, this.parentModel);
                } else {
                    if (dependency.value === "__same__") {
                        newValue = remoteValue;
                    } else {
                        newValue = dependency.value;
                    }
                }
                if (this.isChanged(component.getValue(), newValue)) {
                    component.value(newValue);
                    let newData = {};
                    newData[component.definition.property] = newValue;
                    holder.updateData(newData);
                }
            }
            if (type === DependencyType.queryParameters && matched) {
                if (Array.isArray(dependency.options) && isComponentWithOptions(component)) {
                    if (dependency.options[0].property != null) {
                        let value = remoteValue as any;
                        // For code searchers
                        if (value != null && value.hasOwnProperty("code")) {
                            value = value.code;
                        }
                        component.addOptions([{
                            property: dependency.options[0].property,
                            value,
                        }]);
                    }
                }
            }
            if (type === DependencyType.disabledOptions) {
                if (Array.isArray(dependency.options) && isComponentThatCanDisableOptions(component)) {
                    if (matched) {
                        component.disableOptions(dependency.options);
                    } else {
                        component.enableOptions(dependency.options);
                    }
                } else {
                    console.warn("Can not apply dependency to component ", dependency, component);
                }
            }

            if (type === DependencyType.validation && holder != null) {
                let r = {};
                r[component.definition.property] = {};
                if (matched) {
                    let validations = JSON.parse(JSON.stringify(dependency.validations ?? []));
                    for (const validation of validations) {
                        // tslint:disable-next-line: forin
                        for (const key in validation["options"]) {
                            const option = validation["options"][key];
                            if (option === "__same__") {
                                validation["options"][key] = remoteValue;
                            }
                            if (option === "__expression__" && isExpressionDependency(dependency)) {
                                validation["options"][key] =
                                    ExpressionDependencyRunner.run(dependency.expression, this.parentModel);
                            }
                        }
                    }
                    r[component.definition.property].validations = validations;
                    holder.initRestrictions(r);
                }
            }
        }
    }

    private isChanged(a, b): boolean {
        if (a === b || a == null && b === "" || a == null && b === undefined || a === undefined && b === null) {
            return false;
        }
        if (a == null && typeof b === "object" && Object.keys(b).length === 0) {
            return false;
        }
        let original = JSON.stringify(a);
        let modified = JSON.stringify(b);
        if (original === modified) {
            return false;
        }
        return true;
    }

    private static setRequired(component: InputComponent, holder: ValidatorComponent, required: boolean) {
        component.setRequired(required);
        holder.setRequired(component.definition.property, required);
    }
    public applyDependenciesToButton(component: ActionButton, holder: ValidatorComponent & DataHolderComponent): void {
        if (this.restrictions == null) {
            return;
        }
        for (let dependency of this.findDependenciesForButton(component)) {
            const matchResult = DependencyEvaluator.or(dependency.conditions, this.parentModel);

            this.applyDependency(
                component,
                dependency,
                matchResult.matches,
                holder,
                matchResult.component,
            );
        }
    }
    private findDependenciesForButton(component: ActionButton): Dependency[] {
        if (this.restrictions[component.definition.id] != null
            && this.restrictions[component.definition.id].dependencies != null) {
            return this.restrictions[component.definition.id].dependencies;
        }
        if (this.restrictions[component.definition.action?.actionName] != null
            && this.restrictions[component.definition.action.actionName].dependencies != null) {
            return this.restrictions[component.definition.action.actionName].dependencies;
        }
        return [];
    }
}
